import React, { useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useQueryClient } from '@tanstack/react-query';
import {
	IAdminTask,
	IAdminTaskFields,
	TASKS_QUERY_KEY,
	TaskStatus,
	TaskSubjectKind,
	TaskType,
	createTask,
	deleteTask,
	isTaskType,
	updateTask,
	useAdminAccountsQuery,
	useAdminTaskAssigneesQuery,
	useAdminTeamsQuery,
	useAdminUsersQuery,
} from '../../api/admin';
import { apiErrorMessage } from '../../utils/api';
import TaskAssigneeLabel from './TaskAssigneeLabel';
import { DEFAULT_TASK_TYPE, TASK_STATUS_OPTIONS, TASK_SUBJECT_KIND_OPTIONS, TASK_TYPE_OPTIONS } from './taskOptions';

interface Props {
	open: boolean;
	onClose: () => void;
	/** When set, the dialog edits this task; otherwise it creates one. */
	task?: IAdminTask | null;
	/** Assignee for a new task, normally the signed-in admin. */
	defaultAssigneeId?: number | null;
	/**
	 * What a new task is tagged to from the start, e.g. the user or account whose page it is opened
	 * from. Pass a stable (memoised) object: the form refills from it whenever it changes.
	 */
	defaultSubject?: { kind: TaskSubjectKind; id: number; name: string } | null;
}

interface SubjectOption {
	id: number;
	label: string;
}

interface FieldErrors {
	title?: string;
	subjects?: string;
}

const today = () => new Date().toLocaleDateString('en-CA');

/** Create or edit a task with every field: type, status, assignee, dates, what it is tagged to, notes. Delete lives here too. */
export default function TaskDialog({ open, onClose, task, defaultAssigneeId = null, defaultSubject = null }: Props) {
	const queryClient = useQueryClient();
	const isEdit = !!task;
	// Reference lists load only while the dialog is open: it is mounted, closed, on every task list.
	const { data: allUsers, isLoading: usersLoading } = useAdminUsersQuery({ enabled: open });
	const { data: allTeams, isLoading: teamsLoading } = useAdminTeamsQuery(undefined, { enabled: open });
	const { data: assignees = [] } = useAdminTaskAssigneesQuery({ enabled: open });

	const [title, setTitle] = useState('');
	const [type, setType] = useState<TaskType>(DEFAULT_TASK_TYPE);
	const [status, setStatus] = useState<TaskStatus>('open');
	const [assigneeId, setAssigneeId] = useState<number | ''>('');
	const [dueDate, setDueDate] = useState('');
	const [completedDate, setCompletedDate] = useState('');
	const [kind, setKind] = useState<TaskSubjectKind | ''>('');
	const [subjects, setSubjects] = useState<SubjectOption[]>([]);
	const [notes, setNotes] = useState('');
	const [errors, setErrors] = useState<FieldErrors>({});
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [confirmDelete, setConfirmDelete] = useState(false);

	// Accounts need manage_accounts as well, so only ask for them once the admin picks Accounts.
	const { data: allAccounts, isError: accountsError } = useAdminAccountsQuery(undefined, {
		enabled: open && kind === 'account',
	});

	const optionsByKind = useMemo<Record<TaskSubjectKind, SubjectOption[]>>(
		() => ({
			user: (allUsers ?? [])
				.filter((u) => u.is_active)
				.map((u) => ({ id: u.id, label: u.full_name ? `${u.full_name} (${u.email})` : u.email }))
				.sort((a, b) => a.label.localeCompare(b.label)),
			team: (allTeams ?? []).map((t) => ({ id: t.id, label: `${t.name} · ${t.organization_name}` })),
			account: (allAccounts ?? []).map((a) => ({ id: a.id, label: `${a.name} · ${a.organization_name}` })),
		}),
		[allUsers, allTeams, allAccounts],
	);

	// (Re)fill the form whenever the dialog opens or the reference data arrives.
	useEffect(() => {
		if (!open) return;
		if (!task) {
			setAssigneeId(defaultAssigneeId ?? '');
			setKind(defaultSubject?.kind ?? '');
			setSubjects(defaultSubject ? [{ id: defaultSubject.id, label: defaultSubject.name }] : []);
			return;
		}
		setTitle(task.title);
		setType(isTaskType(task.type) ? task.type : DEFAULT_TASK_TYPE);
		setStatus(task.status);
		setAssigneeId(task.assignee_user_id ?? '');
		setDueDate(task.due_date ?? '');
		setCompletedDate(task.completed_date ?? '');
		setNotes(task.notes ?? '');
		setKind(task.subject_kind ?? '');
		// matched to the option lists by id, so these labels only need to be readable
		setSubjects(task.subjects.map((s) => ({ id: s.id, label: s.name })));
	}, [open, task, defaultAssigneeId, defaultSubject]);

	function resetAndClose() {
		setTitle('');
		setType(DEFAULT_TASK_TYPE);
		setStatus('open');
		setAssigneeId('');
		setDueDate('');
		setCompletedDate('');
		setKind('');
		setSubjects([]);
		setNotes('');
		setErrors({});
		setError(null);
		setConfirmDelete(false);
		onClose();
	}

	function validate(): boolean {
		const next: FieldErrors = {};
		if (!title.trim()) next.title = 'Required';
		if (kind && subjects.length === 0) next.subjects = 'Pick at least one, or set "Tag to" back to nothing';
		setErrors(next);
		return Object.keys(next).length === 0;
	}

	async function handleSubmit() {
		if (!validate()) return;
		setSubmitting(true);
		setError(null);
		const fields: IAdminTaskFields = {
			title: title.trim(),
			type,
			status,
			notes: notes.trim() || null,
			subject_kind: kind || null,
			subject_ids: kind ? subjects.map((s) => s.id) : [],
			assignee_user_id: assigneeId === '' ? null : assigneeId,
			due_date: dueDate || null,
			completed_date: status === 'done' ? completedDate || today() : null,
		};
		try {
			if (task) {
				await updateTask(task.id, fields);
			} else {
				await createTask(fields);
			}
			await queryClient.invalidateQueries(TASKS_QUERY_KEY);
			resetAndClose();
		} catch (e: any) {
			setError(apiErrorMessage(e, 'An unexpected error occurred. Please try again.'));
		} finally {
			setSubmitting(false);
		}
	}

	async function handleDelete() {
		if (!task) return;
		setSubmitting(true);
		setError(null);
		try {
			await deleteTask(task.id);
			await queryClient.invalidateQueries(TASKS_QUERY_KEY);
			resetAndClose();
		} catch (e: any) {
			setError(apiErrorMessage(e, 'Could not delete the task. Please try again.'));
			setConfirmDelete(false);
		} finally {
			setSubmitting(false);
		}
	}

	const subjectOptions = kind ? optionsByKind[kind] : [];
	const subjectsLoading = kind === 'user' ? usersLoading : kind === 'team' ? teamsLoading : false;

	return (
		<Dialog open={open} onClose={resetAndClose} maxWidth="sm" fullWidth PaperProps={{ sx: { backgroundImage: 'none' } }}>
			<DialogTitle>{isEdit ? 'Edit Task' : 'New Task'}</DialogTitle>
			<DialogContent sx={{ pb: 1 }}>
				{error && (
					<Alert severity="error" sx={{ mb: 2 }}>
						{error}
					</Alert>
				)}
				<Stack spacing={2} sx={{ mt: 1.5 }}>
					<TextField
						label="Title"
						size="small"
						required
						autoFocus={!isEdit}
						value={title}
						onChange={(e) => {
							setTitle(e.target.value);
							setErrors((er) => ({ ...er, title: undefined }));
						}}
						error={!!errors.title}
						helperText={errors.title}
						inputProps={{ maxLength: 200 }}
					/>
					<Box sx={{ display: 'flex', gap: 2 }}>
						<TextField
							select
							label="Type"
							size="small"
							value={type}
							onChange={(e) => setType(e.target.value as TaskType)}
							sx={{ flex: 1 }}
						>
							{TASK_TYPE_OPTIONS.map((o) => (
								<MenuItem key={o.value} value={o.value}>
									{o.label}
								</MenuItem>
							))}
						</TextField>
						<TextField
							select
							label="Status"
							size="small"
							value={status}
							onChange={(e) => setStatus(e.target.value as TaskStatus)}
							sx={{ flex: 1 }}
						>
							{TASK_STATUS_OPTIONS.map((o) => (
								<MenuItem key={o.value} value={o.value}>
									{o.label}
								</MenuItem>
							))}
						</TextField>
					</Box>
					<Box sx={{ display: 'flex', gap: 2 }}>
						<TextField
							select
							label="Assignee"
							size="small"
							value={assigneeId}
							onChange={(e) => setAssigneeId(e.target.value === '' ? '' : Number(e.target.value))}
							sx={{ flex: 1 }}
						>
							<MenuItem value="">Unassigned</MenuItem>
							{assignees.map((u) => (
								<MenuItem key={u.id} value={u.id}>
									<TaskAssigneeLabel user={u} assignees={assignees} />
								</MenuItem>
							))}
						</TextField>
						<TextField
							label="Due"
							type="date"
							size="small"
							value={dueDate}
							onChange={(e) => setDueDate(e.target.value)}
							InputLabelProps={{ shrink: true }}
							sx={{ flex: 1 }}
						/>
						{status === 'done' && (
							<TextField
								label="Completed on"
								type="date"
								size="small"
								value={completedDate}
								onChange={(e) => setCompletedDate(e.target.value)}
								helperText="blank = today"
								InputLabelProps={{ shrink: true }}
								sx={{ flex: 1 }}
							/>
						)}
					</Box>
					<Box sx={{ display: 'flex', gap: 2 }}>
						<TextField
							select
							label="Tag to"
							size="small"
							value={kind}
							onChange={(e) => {
								setKind(e.target.value as TaskSubjectKind | '');
								setSubjects([]); // one kind per task
								setErrors((er) => ({ ...er, subjects: undefined }));
							}}
							sx={{ width: 150, flexShrink: 0 }}
						>
							<MenuItem value="">Nothing</MenuItem>
							{TASK_SUBJECT_KIND_OPTIONS.map((o) => (
								<MenuItem key={o.value} value={o.value}>
									{o.label}
								</MenuItem>
							))}
						</TextField>
						<Autocomplete
							multiple
							options={subjectOptions}
							loading={subjectsLoading}
							disableCloseOnSelect
							filterSelectedOptions
							disabled={!kind}
							value={subjects}
							onChange={(_, v) => {
								setSubjects(v);
								setErrors((er) => ({ ...er, subjects: undefined }));
							}}
							getOptionLabel={(o) => o.label}
							isOptionEqualToValue={(a, b) => a.id === b.id}
							renderTags={(selected, getTagProps) =>
								selected.map((o, index) => <Chip {...getTagProps({ index })} label={o.label} size="small" />)
							}
							renderInput={(params) => (
								<TextField
									{...params}
									label={kind ? TASK_SUBJECT_KIND_OPTIONS.find((o) => o.value === kind)?.label : 'Subjects'}
									size="small"
									placeholder={kind ? 'Pick one or more…' : 'Pick what to tag first'}
									error={!!errors.subjects}
									helperText={
										errors.subjects ??
										(kind === 'account' && accountsError ? 'Accounts could not be loaded (needs the accounts permission)' : undefined)
									}
								/>
							)}
							sx={{ flex: 1 }}
						/>
					</Box>
					<TextField
						label="Notes"
						size="small"
						multiline
						minRows={3}
						placeholder="e.g. emailed the QTD tracker · call booked for Thursday"
						value={notes}
						onChange={(e) => setNotes(e.target.value)}
					/>
				</Stack>
			</DialogContent>
			<DialogActions sx={{ px: 3, pb: 2 }}>
				{isEdit &&
					(confirmDelete ? (
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 'auto' }}>
							<Typography variant="body2">Delete this task?</Typography>
							<Button color="error" size="small" onClick={handleDelete} disabled={submitting}>
								Delete
							</Button>
							<Button size="small" onClick={() => setConfirmDelete(false)} disabled={submitting}>
								Keep
							</Button>
						</Box>
					) : (
						<Button
							color="error"
							startIcon={<DeleteOutlineIcon />}
							onClick={() => setConfirmDelete(true)}
							disabled={submitting}
							sx={{ mr: 'auto' }}
						>
							Delete
						</Button>
					))}
				<Button onClick={resetAndClose} disabled={submitting}>
					Cancel
				</Button>
				<Button
					variant="contained"
					onClick={handleSubmit}
					disabled={submitting}
					startIcon={submitting ? <CircularProgress size={16} /> : undefined}
				>
					{submitting ? 'Saving…' : isEdit ? 'Save' : 'Create Task'}
				</Button>
			</DialogActions>
		</Dialog>
	);
}
